import {Box, CssBaseline, Stack, Typography, useColorScheme} from "@mui/material";
import React, {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from "react";
import {Experimental_CssVarsProvider as CssVarsProvider, StyledEngineProvider} from "@mui/material/styles";
import StripeProvider from "../../../hooks/use-stripe";
import useQuery from "../../../hooks/use-query";
import useIsMounted from "../../../hooks/use-is-mounted";
import axios from "axios";
import theme from "../../../theme";
import NewStripeCreditCardForm from "../../../components/app-specific/new-credit-card-form/stripe";
import Button from "../../../components/base/button/button";
import {useHistory} from "react-router-dom";
import {routes} from "../../../../core/models/static/routes/app-routes";
import {CreditCardProviders} from "../../../../core/constants/enums";
import EnvService from "../../../../core/services/env";

class Logger {
    constructor(notifyListeners) {
        this._logs = [];
        this._notifyListeners = notifyListeners;
    }

    log(...messages) {
        this._logs.push(...messages);
        this._notifyListeners?.();
    }

    get logs() {
        return this._logs;
    }
}


const PhoneStripeCardSetupClientSideView = () => {
    const [colorTheme, setColorTheme] = useState("{}");
    const [query] = useQuery();
    const {replace: historyReplace} = useHistory();
    const [requests, setRequests] = useState([]);
    const [responses, setResponses] = useState([]);
    const isMounted = useIsMounted();

    const authToken = useMemo(() => query.authToken, [query.authToken]);
    const os = useMemo(() => query.os, [query.os]);
    const partnerId = useMemo(() => query.partnerId, [query.partnerId]);
    const colorMode = useMemo(() => query.colorMode, [query.colorMode]);
    const successUrl = useMemo(() => query.successUrl, [query.successUrl]);
    const errorUrl = useMemo(() => query.errorUrl, [query.errorUrl]);
    const passedTheme = useMemo(() => query.theme, [query.theme]);
    const generatedTheme = useMemo(() => theme(colorTheme), [colorTheme]);
    const [, setRefersher] = useState(false);
    const notifyListeners = useCallback(() => setRefersher(prevState => !prevState), []);

    const logger = useMemo(() => new Logger(notifyListeners), [notifyListeners]);

    const missingCriticalParams = useMemo(() => {
        return !authToken || !successUrl || !partnerId;
    }, [authToken, partnerId, successUrl])

    /**
     * Redirects to the error URL if any of the critical parameters are missing.
     */
    useLayoutEffect(() => {
        if (missingCriticalParams && errorUrl) {
            window.location = errorUrl;
        } else if (missingCriticalParams) {
            historyReplace(routes.error.notFound + "?hideBackButton=true");
        }
    }, [missingCriticalParams, historyReplace, errorUrl]);

    // -------------------------------------------------- API CALL SUBS -------------------------------------------------- //

    /**
     * Fetches the list of payment providers from the server.
     * @type {(function(*): Promise<*>)|*}
     */
    const getPaymentProviders = useCallback(async () => {
        try {
            setRequests(prevState => [
                ...prevState,
                {
                    endpoint: EnvService.ClientApiUrl + "/api/CustomerCard/GetPaymentProviders",
                    method: "GET",
                    timestamp: new Date().toISOString(),
                    headers: {Authorization: authToken, PartnerId: partnerId, OS: os}
                }
            ])
            const response = await axios.get(EnvService.ClientApiUrl + "/api/CustomerCard/GetPaymentProviders",
                {headers: {Authorization: authToken, PartnerId: partnerId, OS: os}}
            );
            setResponses(prev => [...prev, response?.data]);
            return response?.data;
        } catch {
            return undefined;
        }
    }, [authToken, os, partnerId])


    /**
     * Fetches the Stripe public key from the server.
     * @type {(function(): Promise<any|undefined>)|*}
     */
    const fetchStripeKey = useCallback(async () => {
        try {
            setRequests(prevState => [
                ...prevState,
                {
                    endpoint: EnvService.ClientApiUrl + "/api/CustomerCard/GetStripePublicKey",
                    method: "GET",
                    timestamp: new Date().toISOString(),
                    headers: {Authorization: authToken, PartnerId: partnerId, OS: os}
                }
            ])
            const response = await axios.get(
                EnvService.ClientApiUrl + "/api/CustomerCard/GetStripePublicKey",
                {headers: {Authorization: authToken, PartnerId: partnerId, OS: os}}
            );
            setResponses(prev => [...prev, response?.data]);
            return response?.data;
        } catch {
            return undefined;
        }
    }, [authToken, partnerId, os]);

    /**
     * Creates a new Stripe credit card by sending the token to the server.
     * @type {(function(*): Promise<any|undefined>)|*}
     */
    const createStripeCreditCard = useCallback(async (forApi) => {
        try {
            setRequests(prevState => [
                ...prevState,
                {
                    endpoint: EnvService.ClientApiUrl + "/api/CustomerCard/AddCardByToken",
                    method: "POST",
                    body: forApi,
                    timestamp: new Date().toISOString(),
                    headers: {Authorization: authToken, PartnerId: partnerId, OS: os}
                }
            ])
            const response = await axios.post(
                EnvService.ClientApiUrl + "/api/CustomerCard/AddCardByToken",
                forApi,
                {headers: {Authorization: authToken, PartnerId: partnerId, OS: os}}
            );
            setResponses(prev => [...prev, response?.data]);
            return response?.data;
        } catch {
            return undefined;
        }
    }, [authToken, os, partnerId])


    /**
     * Fetches the color theme and logo url of the current partner
     * @type {(function(*): Promise<void>)}
     */
    const parseTheme = useCallback((passedTheme) => {
        if (!passedTheme) {
            return;
        }
        try {
            const parsed = JSON.parse(decodeURIComponent(passedTheme));
            const colors = parsed?.colors ?? [];
            setColorTheme(JSON.stringify({
                light: {
                    palette: {
                        primary: {main: colors?.find(e => e.name === 'blue')?.colorCode},
                        secondary: {main: colors?.find(e => e.name === 'redLogo')?.colorCode},
                    },
                },
            }));
        } catch {
        }
    }, [])

    /**
     * Listens for the color theme fetched event and sets the color theme
     */
    useEffect(() => {
        if (!passedTheme)
            return;
        void parseTheme(passedTheme);
    }, [passedTheme, parseTheme]);

    // -------------------------------------------------- NORMAL FLOW -------------------------------------------------- //

    /**
     * Fetches the list of credit card providers from the server.
     * @type {(function(*): Promise<void>)}
     */
    const getCreditCardProviders = useCallback(async (cancelToken) => {
        const response = await getPaymentProviders(cancelToken);
        if (!isMounted())
            return;
        const hasStripe = response?.data?.some(provider => provider.name === CreditCardProviders.stripe);
        if (!hasStripe && errorUrl) {
            historyReplace(routes.error.accessDenied + "?hideBackButton=true");
        }
    }, [getPaymentProviders, isMounted, errorUrl, historyReplace])

    /**
     * Listens for the credit card providers fetched event and checks whether stripe is enabled.
     */
    useEffect(() => {
        if (!authToken)
            return;
        const cancelTokenSource = axios.CancelToken.source();
        void getCreditCardProviders(cancelTokenSource.token);
        return () => {
            cancelTokenSource.cancel();
        }
    }, [authToken, getCreditCardProviders]);


    /**
     * Handles the success callback of the credit card form.
     * @type {(function(*): void)}
     */
    const onSuccess = useCallback(() => {
        window.location = successUrl;
    }, [successUrl])

    return (
        <>
            <div data-mui-color-scheme="light">
                <CssVarsProvider
                    defaultMode={colorMode ?? 'system'}
                    theme={generatedTheme}
                    modeStorageKey={'phone-stripe-card-setup-color-scheme'}
                    colorSchemeStorageKey={'phone-stripe-card-setup-color-scheme'}
                >
                    <CssBaseline enableColorScheme/>
                    <StyledEngineProvider injectFirst>
                        <StripeProvider authToken={authToken} bypassAuthCheck fetchStripeKey={fetchStripeKey}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    minHeight: '100dvh',
                                    maxWidth: '100vw',
                                    padding: 2,
                                    '@supports (-webkit-touch-callout: none)': {
                                        minHeight: '-webkit-fill-available',
                                    },
                                }}>
                                <PhoneStripeCardSetupClientSide
                                    colorMode={colorMode}
                                    onSuccess={onSuccess}
                                    submitFunction={createStripeCreditCard}
                                />
                                {/*<Stack sx={{width: '100%'}} spacing={2}>*/}
                                {/*    <Box>*/}
                                {/*        <Typography variant={'h6'} fontWeight={600} gutterBottom>*/}
                                {/*            VARS:*/}
                                {/*        </Typography>*/}
                                {/*        <JsonRenderer data={{*/}
                                {/*            authToken,*/}
                                {/*            os,*/}
                                {/*            partnerId,*/}
                                {/*            colorMode,*/}
                                {/*            successUrl,*/}
                                {/*            errorUrl,*/}
                                {/*            passedTheme*/}
                                {/*        }}/>*/}
                                {/*    </Box>*/}
                                {/*    <Box>*/}
                                {/*        <Typography variant={'h6'} fontWeight={600} gutterBottom>*/}
                                {/*            Requests:*/}
                                {/*        </Typography>*/}
                                {/*        {requests.map((request, index) => (*/}
                                {/*            <Box key={index} sx={{marginBottom: 2}}>*/}
                                {/*                <Typography variant={'body2'} fontWeight={500} gutterBottom>*/}
                                {/*                    Request {index + 1}:*/}
                                {/*                </Typography>*/}
                                {/*                <JsonRenderer data={request}/>*/}
                                {/*            </Box>*/}
                                {/*        ))}*/}
                                {/*    </Box>*/}
                                {/*    <Box>*/}
                                {/*        <Typography variant={'h6'} fontWeight={600} gutterBottom>*/}
                                {/*            Responses:*/}
                                {/*        </Typography>*/}
                                {/*        {responses.map((response, index) => (*/}
                                {/*            <Box key={index} sx={{marginBottom: 2}}>*/}
                                {/*                <Typography variant={'body2'} fontWeight={500} gutterBottom>*/}
                                {/*                    Response {index + 1}:*/}
                                {/*                </Typography>*/}
                                {/*                <JsonRenderer data={response}/>*/}
                                {/*            </Box>*/}
                                {/*        ))}*/}
                                {/*    </Box>*/}
                                {/*    <Box>*/}
                                {/*        <Typography variant={'h6'} fontWeight={600} gutterBottom>*/}
                                {/*            Logger:*/}
                                {/*        </Typography>*/}
                                {/*        <JsonRenderer data={logger.logs}/>*/}
                                {/*    </Box>*/}
                                {/*</Stack>*/}
                            </Box>
                        </StripeProvider>
                    </StyledEngineProvider>
                </CssVarsProvider>
            </div>
        </>
    );
}

const PhoneStripeCardSetupClientSide = ({colorMode, onSuccess, submitFunction}) => {
    const {setMode} = useColorScheme();
    const [submitting, setSubmitting] = useState(false);
    const formRef = useRef();

    /**
     * Sets the color mode based on the query parameter if it is different from the current mode.
     */
    useEffect(() => {
        if (colorMode) setMode(colorMode);
    }, [colorMode, setMode]);

    /**
     * Submits the new credit card form with the option of saving its data.
     * @type {function(): Promise<void>}
     */
    const addCreditCard = useCallback(async () => {
        await formRef.current?.submit(true);
    }, []);

    return (
        <Box
            data-mui-color-scheme={colorMode ?? 'light'}
            sx={{display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'}}
        >
            <Stack flexDirection={'column'} alignItems={'center'} justifyContent={'center'}>
                <Typography variant={'h5'} fontWeight={600} gutterBottom textAlign={'start'} width={'100%'}>
                    Credit Card Information
                </Typography>

                <Typography variant={'body2'} fontWeight={500} gutterBottom>
                    Please enter the customer credit card information to be saved for future transactions.
                </Typography>
                <NewStripeCreditCardForm
                    submitFunction={submitFunction}
                    onSuccess={onSuccess}
                    setSubmitting={setSubmitting}
                    className={'w-100'}
                    ref={formRef}
                />
                <Button disabled={submitting} variant={'contained'} color={'primary'} onClick={addCreditCard} size={'large'} sx={{marginBlockStart: 3, minWidth: 200}}>
                    {submitting ? "Adding..." : 'Add'}
                </Button>
            </Stack>

        </Box>
    );
}

export default PhoneStripeCardSetupClientSideView;